import React from "react";
import Stack from '@mui/material/Stack';
import {SignupStages} from './components/SignupStages';
import {SignupStep0} from "./components/SignupStep0";
import {SignupStep1} from "./components/SignupStep1";
import {SignupStep2} from "./components/SignupStep2";
import {SignupStep3} from "./components/SignupStep3";
import {SignupStep4} from "./components/SignupStep4";
import { useIdentityContext } from "../../context/IdentityContext";

export interface ISignupData
{
  subscriptionId : string;
}

export interface ISentinelDetails
{
    subscriptionId: string;
}

export interface IValidSentinelInstance 
{
    subscriptionId : string;
    resourceGroupName : string;
    workspaceId : string;
    workspaceName : string;
}

export interface IPrimaryContact
{
  firstName: string;
  surname: string;
  email: string;
  emailConfirm: string;
  objectId : string;
}

export interface ICompanyDetails
{
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  townCity: string | null;
  stateCounty: string | null;
  postalZip: string | null;
  country: string | null;
  tenantId : string;
}

export interface INewAccountData {
  companyDetails : ICompanyDetails
  contactDetails : IPrimaryContact;
  sentinelDetails : IValidSentinelInstance;
}

export const SignupPage: React.FC = () => {
  
  const [activeStep, setActiveStep] = React.useState(0);
  const { authenticatedUser } = useIdentityContext();
  
  const [newAccountData, setNewAccountData] = React.useState<INewAccountData>({
    sentinelDetails: {
      subscriptionId : "",
      resourceGroupName : "",
      workspaceId : "",
      workspaceName : ""
    },
    companyDetails: {
      name: "",
      addressLine1: null,
      addressLine2: null,
      addressLine3: null,
      townCity: null,
      stateCounty: null,
      postalZip: null,
      country: null,
      tenantId: authenticatedUser.tenantId
    },
    contactDetails: {
      firstName: "",
      surname: "",
      email: "",
      emailConfirm: "",
      objectId: authenticatedUser.objectId
    }
  });

  const setSentinelData = (data: IValidSentinelInstance) => {
    setNewAccountData({ ...newAccountData, sentinelDetails: data });
    setActiveStep(1);
  };

  const setCompanyData = (data: ICompanyDetails) => {
    setNewAccountData({
      ...newAccountData,
      companyDetails: data });
    setActiveStep(2);
  };

  const setContactData = (data: IPrimaryContact) => {
    setNewAccountData({
      ...newAccountData,
      contactDetails: data
    });
    setActiveStep(3);
  };

  return (
      <Stack
        direction="column"
        component="main"
        sx={[
          {
            justifyContent: 'center',
            height: 'calc((1 - var(--template-frame-height, 0)) * 100%)',
            marginTop: 'max(40px - var(--template-frame-height, 0px), 0px)',
            minHeight: '100%',
          },
          (theme) => ({
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              zIndex: -1,
              inset: 0,
              backgroundImage:
                'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
              backgroundRepeat: 'no-repeat',
              ...theme.applyStyles('dark', {
                backgroundImage:
                  'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
              }),
            },
          }),
        ]}
      >
        <Stack
          direction={{ xs: 'column-reverse', md: 'row' }}
          sx={{
            justifyContent: 'center',
            gap: { xs: 6, sm: 12 },
            p: 2,
            mx: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column-reverse', md: 'row' }}
            sx={{
              justifyContent: 'center',
              gap: { xs: 6, sm: 12 },
              p: { xs: 2, sm: 4 },
              m: 'auto',
            }}
          >
            <SignupStages currentStage={activeStep} />
            
           {(() => {
              switch (activeStep) {
              case 0:
                return <SignupStep0 onNextClick={() => { setActiveStep(1) }} />;
              case 1:
                return <SignupStep1 onNextClick={(data : IValidSentinelInstance) => { 
                  setSentinelData(data);
                  setActiveStep(2) 
                }}
                  formData={newAccountData.sentinelDetails}
                  onPreviousClick={() => { setActiveStep(0); }}
                />
              case 2:
                return <SignupStep2 onNextClick={(data : ICompanyDetails) => { 
                  setCompanyData(data);
                  setActiveStep(3) 
                }}
                  formData={newAccountData.companyDetails}
                  onPreviousClick={() => { setActiveStep(1); }}
                />
              case 3:
                return <SignupStep3 onNextClick={(data: IPrimaryContact) => { 
                  setContactData(data);
                  setActiveStep(4) 
                }}
                  formData={newAccountData.contactDetails}
                  onPreviousClick={() => { setActiveStep(2); }}
                />
               case 4:
                  return <SignupStep4
                  formData={newAccountData}
                  onPreviousClick={() => { setActiveStep(3); }}
                />
              
              default:
                return <></>;
              }
            })()}

          </Stack>
        </Stack>
      </Stack>
  )
}
